import { Panel } from '@xyflow/react'
import { FC } from 'react'

import { MemoFlxButton } from '@flowx/react-ui-toolkit'

export interface LayoutPreviewBarProps {
  summary: string
  showCurrent: boolean
  /** The diagram changed after the preview was planned: offer Refresh instead of Apply. */
  stale: boolean
  /** Saving or re-planning: every action waits. */
  busy: boolean
  canApply: boolean
  onToggleShowCurrent: () => void
  onCancel: () => void
  onApply: () => void
  onRefresh: () => void
  /** Prefix for the bar's data-testids, e.g. `bpmn-canvas-layout-preview`. */
  testId: string
}

/**
 * The confirmation for an auto-arrange preview, pinned to the top of a React Flow canvas: it
 * leaves the canvas free to pan and zoom while the user inspects the previewed layout.
 */
const LayoutPreviewBar: FC<LayoutPreviewBarProps> = ({
  summary,
  showCurrent,
  stale,
  busy,
  canApply,
  onToggleShowCurrent,
  onCancel,
  onApply,
  onRefresh,
  testId,
}) => (
  <Panel
    position="top-center"
    className="nopan nodrag nowheel"
    data-testid={testId}
  >
    <div className="flex items-center gap-3 whitespace-nowrap rounded-lg bg-white px-4 py-2 text-sm shadow-[0_4px_16px_0_rgba(15,17,20,0.1)]">
      <i className="ph ph-align-left text-neutral-900"></i>
      <span className="font-semibold text-neutral-900">Layout preview</span>
      <span
        className="text-neutral-600"
        data-testid={`${testId}-summary`}
      >
        {summary}
      </span>
      <MemoFlxButton
        className="flx-ds small ternary"
        onClick={onToggleShowCurrent}
        disabled={busy}
        aria-pressed={showCurrent}
        data-testid={`${testId}-show-current`}
      >
        {showCurrent ? 'Show preview' : 'Show current'}
      </MemoFlxButton>
      <MemoFlxButton
        className="flx-ds small secondary"
        onClick={onCancel}
        disabled={busy}
        data-testid={`${testId}-cancel`}
      >
        Cancel
      </MemoFlxButton>
      {stale ? (
        <MemoFlxButton
          className="flx-ds small primary"
          onClick={onRefresh}
          disabled={busy}
          data-testid={`${testId}-refresh`}
        >
          Refresh preview
        </MemoFlxButton>
      ) : (
        <MemoFlxButton
          className="flx-ds small primary"
          onClick={onApply}
          disabled={busy || !canApply}
          data-testid={`${testId}-apply`}
        >
          Apply
        </MemoFlxButton>
      )}
    </div>
  </Panel>
)

export default LayoutPreviewBar
